import { useCallback } from 'react';

/**
 * Hook to fire Google Analytics 4 eCommerce events.
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag
 */
export function useGaEvents() {
  const trackEvent = useCallback((eventName: string, params?: any) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    } catch (error) {
      console.warn(`[GA4] Failed to track event: ${eventName}`, error);
    }
  }, []);

  /**
   * Tracks a product view
   */
  const trackViewItem = useCallback((product: any) => {
    if (!product) return;
    
    const price = product.variants?.[0]?.priceWithTax || product.price?.value || product.price || 0;
    
    trackEvent('view_item', {
      currency: 'VND',
      value: price / 100, // Normalized to major currency unit if needed, Vendure uses cents
      items: [
        {
          item_id: product.id || product.slug,
          item_name: product.name,
          price: price / 100,
          quantity: 1,
        },
      ],
    });
  }, [trackEvent]);

  /**
   * Tracks adding an item to cart
   */
  const trackAddToCart = useCallback((variant: any, quantity: number = 1) => {
    if (!variant) return;
    
    const price = variant.priceWithTax || variant.price?.value || variant.price || 0;
    
    trackEvent('add_to_cart', {
      currency: 'VND',
      value: (price / 100) * quantity,
      items: [
        {
          item_id: variant.id,
          item_name: variant.name,
          price: price / 100,
          quantity: quantity,
        },
      ],
    });
  }, [trackEvent]);

  /**
   * Tracks a successful purchase
   */
  const trackPurchase = useCallback((order: any) => {
    if (!order) return;
    
    trackEvent('purchase', {
      transaction_id: order.code,
      value: order.totalWithTax / 100,
      tax: (order.taxSummary?.[0]?.taxTotal || 0) / 100,
      shipping: (order.shippingWithTax || 0) / 100,
      currency: order.currencyCode || 'VND',
      items: order.lines?.map((line: any) => ({
        item_id: line.productVariant.id,
        item_name: line.productVariant.name,
        price: line.unitPriceWithTax / 100,
        quantity: line.quantity,
      })),
    });
  }, [trackEvent]);

  const trackScheduledOrder = useCallback((plan: any) => {
    if (!plan?.code || typeof window === 'undefined') return;

    const storageKey = `ga:schedule_order:${plan.code}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) return;
      trackEvent('schedule_order', {
        transaction_id: plan.code,
        value: (plan.totalWithTax || 0) / 100,
        currency: plan.currencyCode || 'NPR',
        payment_method: plan.paymentMethod,
        occurrence_count: plan.occurrences?.length || 0,
      });
      window.sessionStorage.setItem(storageKey, '1');
    } catch (error) {
      console.warn('[GA4] Failed to deduplicate schedule_order', error);
    }
  }, [trackEvent]);

  return {
    trackEvent,
    trackViewItem,
    trackAddToCart,
    trackPurchase,
    trackScheduledOrder,
  };
}
